import { supabase } from '@/lib/supabase/client'

const publicKey = import.meta.env.VITE_PUSH_VAPID_PUBLIC_KEY?.trim()
const localPromptSeenKey = 'outreach-os.notifications-prompt-seen'
// Keep a previous "Not now" choice respected after this one-time prompt
// behaviour is released.
const legacyDismissedKey = 'outreach-os.notifications-prompt-dismissed'

export type NotificationSetupState =
  | 'unsupported'
  | 'unconfigured'
  | 'blocked'
  | 'default'
  | 'enabled'

function publicKeyBytes(key: string): Uint8Array<ArrayBuffer> {
  const normalized = `${key}${'='.repeat((4 - key.length % 4) % 4)}`.replace(/-/g, '+').replace(/_/g, '/')
  const binary = atob(normalized)
  return Uint8Array.from(binary, (character) => character.charCodeAt(0))
}

function subscriptionParts(subscription: PushSubscription) {
  const json = subscription.toJSON()
  const endpoint = json.endpoint ?? subscription.endpoint
  const p256dh = json.keys?.p256dh
  const auth = json.keys?.auth
  if (!endpoint || !p256dh || !auth) throw new Error('The browser returned an incomplete notification subscription.')
  return { endpoint, p256dh, auth }
}

export function notificationSetupState(permission = typeof Notification === 'undefined' ? undefined : Notification.permission): NotificationSetupState {
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || typeof Notification === 'undefined') return 'unsupported'
  if (!publicKey) return 'unconfigured'
  if (permission === 'denied') return 'blocked'
  return permission === 'granted' ? 'enabled' : 'default'
}

/**
 * An iPhone or iPad using OutreachOS in a browser tab: Apple only lets web
 * apps added to the Home Screen receive notifications.
 */
export function needsHomeScreenForNotifications(): boolean {
  const apple = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.userAgent.includes('Macintosh') && navigator.maxTouchPoints > 1)
  const installed = (typeof window.matchMedia === 'function' && window.matchMedia('(display-mode: standalone)').matches) || (navigator as Navigator & { standalone?: boolean }).standalone === true
  return apple && !installed
}

/**
 * Whether this device actually receives broadcasts. The browser's permission
 * alone can't say: Disable unsubscribes this device, but a site can never take
 * back a permission the browser granted, so "granted" stays. Enabled means
 * granted and subscribed; granted without a subscription is off.
 */
export async function currentNotificationState(): Promise<NotificationSetupState> {
  const state = notificationSetupState()
  if (state !== 'enabled') return state
  try {
    const registration = await navigator.serviceWorker.getRegistration()
    return (await registration?.pushManager.getSubscription()) ? 'enabled' : 'default'
  } catch {
    return 'default'
  }
}

export function shouldOfferNotificationPrompt(): boolean {
  return notificationSetupState() === 'default'
    && window.localStorage.getItem(localPromptSeenKey) !== 'true'
    && window.localStorage.getItem(legacyDismissedKey) !== 'true'
}

/** Marks the automatic prompt as seen. Notification setup remains in Account. */
export function markNotificationPromptSeen(): void {
  window.localStorage.setItem(localPromptSeenKey, 'true')
}

export async function enableCurrentDeviceNotifications(): Promise<void> {
  if (notificationSetupState() === 'unsupported') throw new Error('This browser does not support notifications.')
  if (notificationSetupState() === 'unconfigured') throw new Error('Notifications are not configured for this environment yet.')
  const permission = Notification.permission === 'granted' ? 'granted' : await Notification.requestPermission()
  if (permission !== 'granted') throw new Error('Notifications are blocked for this site. Change the browser site setting to enable them later.')
  const registration = await navigator.serviceWorker.ready
  const subscription = await registration.pushManager.getSubscription() ?? await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: publicKeyBytes(publicKey!),
  })
  const parts = subscriptionParts(subscription)
  const { error } = await supabase.rpc('upsert_push_subscription', {
    subscription_endpoint: parts.endpoint,
    subscription_p256dh: parts.p256dh,
    subscription_auth: parts.auth,
  })
  if (error) throw new Error('This device could not be set up for Session broadcasts.')
}

export async function disableCurrentDeviceNotifications(): Promise<void> {
  const registration = await navigator.serviceWorker.ready
  const subscription = await registration.pushManager.getSubscription()
  if (!subscription) return
  const { error } = await supabase.rpc('remove_current_push_subscription', { subscription_endpoint: subscription.endpoint })
  if (error) throw new Error('This device could not be removed from Session broadcasts.')
  await subscription.unsubscribe()
}
